import { useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CheckCircle, AlertCircle, Package, Send } from 'lucide-react';
import { publicService } from '@/services/publicService';
import { Skeleton } from '@/components/ui/Skeleton';
import type { CaptureLeadRequest, PublicPackage } from '@/types/public';
import styles from './CampaignWidgetPage.module.css';

/**
 * Applies a phone mask: (00) 00000-0000 or (00) 0000-0000.
 */
function applyPhoneMask(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Formats a price value in BRL currency.
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Formats a date string (YYYY-MM-DD) to pt-BR short format.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('pt-BR');
}

/**
 * Embeddable campaign widget page.
 *
 * Standalone page designed for embedding via iframe on clinic websites.
 * No AdminLayout or sidebar - completely self-contained.
 *
 * Features:
 * - Fetches campaign by slug from public API
 * - Displays campaign info and packages with procedure lists
 * - Lead capture form with name, phone, email, notes
 * - UTM parameter capture from URL query string
 * - Mobile-first responsive design
 * - Clinic Fares branding
 * - Success state after form submission
 *
 * Route: /widget/campaign/:slug
 */
export function CampaignWidgetPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  // UTM parameters from URL
  const utmParams = useMemo(
    () => ({
      utmSource: searchParams.get('utm_source') ?? undefined,
      utmMedium: searchParams.get('utm_medium') ?? undefined,
      utmCampaign: searchParams.get('utm_campaign') ?? undefined,
    }),
    [searchParams],
  );

  // Form state
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);

  const {
    data: campaign,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['public-campaign', slug],
    queryFn: () => publicService.getCampaignBySlug(slug!),
    enabled: Boolean(slug),
  });

  const leadMutation = useMutation({
    mutationFn: (data: CaptureLeadRequest) => publicService.captureLead(data),
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: () => {
      setFormError('Erro ao enviar seus dados. Tente novamente.');
    },
  });

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormPhone(applyPhoneMask(e.target.value));
    },
    [],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);

      if (!formName.trim()) {
        setFormError('Nome é obrigatório.');
        return;
      }

      const phoneDigits = formPhone.replace(/\D/g, '');
      if (!phoneDigits || phoneDigits.length < 10) {
        setFormError('Telefone inválido. Informe com DDD.');
        return;
      }

      if (formEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail.trim())) {
        setFormError('E-mail inválido.');
        return;
      }

      const payload: CaptureLeadRequest = {
        name: formName.trim(),
        phone: phoneDigits,
        email: formEmail.trim() || undefined,
        campaignSlug: slug,
        packageId: selectedPackageId ?? undefined,
        notes: formNotes.trim() || undefined,
        ...utmParams,
      };

      leadMutation.mutate(payload);
    },
    [formName, formPhone, formEmail, formNotes, slug, selectedPackageId, utmParams, leadMutation],
  );

  const handleSelectPackage = useCallback((packageId: string) => {
    setSelectedPackageId((prev) => (prev === packageId ? null : packageId));
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.widget}>
        <div className={styles.widgetContent}>
          <div className={styles.headerSkeleton}>
            <Skeleton variant="text" lines={2} />
          </div>
          <Skeleton variant="card" height="200px" />
          <Skeleton variant="card" height="200px" />
        </div>
      </div>
    );
  }

  // Error state
  if (queryError || !campaign) {
    return (
      <div className={styles.widget}>
        <div className={styles.widgetContent}>
          <div className={styles.errorState} role="alert">
            <AlertCircle size={48} className={styles.errorIcon} aria-hidden="true" />
            <h2 className={styles.errorTitle}>Campanha não encontrada</h2>
            <p className={styles.errorMessage}>
              A campanha solicitada não está disponível ou não existe.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state after lead capture
  if (isSuccess) {
    return (
      <div className={styles.widget}>
        <div className={styles.widgetContent}>
          <div className={styles.successState} role="status">
            <CheckCircle size={56} className={styles.successIcon} aria-hidden="true" />
            <h2 className={styles.successTitle}>Interesse registrado!</h2>
            <p className={styles.successMessage}>
              Recebemos seus dados com sucesso. Nossa equipe entrara em contato
              em breve para agendar seus procedimentos.
            </p>
            <div className={styles.successBranding}>
              <span className={styles.logoIcon} aria-hidden="true">+</span>
              <span className={styles.logoText}>
                CRM<span className={styles.logoAccent}>Clinica</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.widget}>
      <div className={styles.widgetContent}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.logo}>
            <span className={styles.logoIcon} aria-hidden="true">+</span>
            <span className={styles.logoText}>
              CRM<span className={styles.logoAccent}>Clinica</span>
            </span>
          </div>
          <h1 className={styles.campaignTitle}>{campaign.name}</h1>
          {campaign.description && (
            <p className={styles.campaignDescription}>{campaign.description}</p>
          )}
          <div className={styles.campaignDates}>
            <span>
              Válida de {formatDate(campaign.startDate)} até {formatDate(campaign.endDate)}
            </span>
          </div>
        </header>

        {/* Packages */}
        {campaign.packages.length > 0 && (
          <section className={styles.packagesSection} aria-labelledby="packages-heading">
            <h2 id="packages-heading" className={styles.sectionTitle}>
              <Package size={20} aria-hidden="true" />
              Pacotes disponíveis
            </h2>
            <ul className={styles.packageList} role="list">
              {campaign.packages.map((pkg: PublicPackage) => (
                <li key={pkg.id}>
                  <button
                    type="button"
                    className={[
                      styles.packageCard,
                      selectedPackageId === pkg.id ? styles.packageCardSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleSelectPackage(pkg.id)}
                    aria-pressed={selectedPackageId === pkg.id}
                    aria-label={`Pacote ${pkg.name} - ${formatCurrency(pkg.price)}`}
                  >
                    <div className={styles.packageHeader}>
                      <h3 className={styles.packageName}>{pkg.name}</h3>
                      <span className={styles.packagePrice}>
                        {formatCurrency(pkg.price)}
                      </span>
                    </div>
                    {pkg.description && (
                      <p className={styles.packageDescription}>{pkg.description}</p>
                    )}
                    {pkg.procedureNames.length > 0 && (
                      <ul className={styles.procedureList} role="list">
                        {pkg.procedureNames.map((name) => (
                          <li key={name} className={styles.procedureItem}>
                            <span className={styles.procedureBullet} aria-hidden="true" />
                            {name}
                          </li>
                        ))}
                      </ul>
                    )}
                    {selectedPackageId === pkg.id && (
                      <span className={styles.selectedBadge}>Selecionado</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Lead capture form */}
        <section className={styles.formSection} aria-labelledby="form-heading">
          <h2 id="form-heading" className={styles.sectionTitle}>
            <Send size={20} aria-hidden="true" />
            Tenho interesse
          </h2>
          <p className={styles.formSubtitle}>
            Preencha seus dados e entraremos em contato.
          </p>

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.formGroup}>
              <label htmlFor="widget-name" className={styles.label}>
                Nome completo <span className={styles.required}>*</span>
              </label>
              <input
                id="widget-name"
                type="text"
                className={styles.input}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Seu nome completo"
                required
                autoComplete="name"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="widget-phone" className={styles.label}>
                Telefone <span className={styles.required}>*</span>
              </label>
              <input
                id="widget-phone"
                type="tel"
                className={styles.input}
                value={formPhone}
                onChange={handlePhoneChange}
                placeholder="(00) 00000-0000"
                required
                autoComplete="tel"
                inputMode="tel"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="widget-email" className={styles.label}>
                E-mail
              </label>
              <input
                id="widget-email"
                type="email"
                className={styles.input}
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="seu@email.com"
                autoComplete="email"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="widget-notes" className={styles.label}>
                Observações
              </label>
              <textarea
                id="widget-notes"
                className={styles.textarea}
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Alguma informação adicional..."
                rows={3}
              />
            </div>

            {formError && (
              <p className={styles.formError} role="alert">
                {formError}
              </p>
            )}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={leadMutation.isPending}
              aria-busy={leadMutation.isPending}
            >
              {leadMutation.isPending ? (
                <>
                  <span className={styles.spinner} aria-hidden="true" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={18} aria-hidden="true" />
                  Quero agendar
                </>
              )}
            </button>
          </form>
        </section>

        {/* Footer branding */}
        <footer className={styles.footer}>
          <span className={styles.footerText}>
            Powered by{' '}
            <span className={styles.footerBrand}>
              CRM<span className={styles.logoAccent}>Clinica</span>
            </span>
          </span>
        </footer>
      </div>
    </div>
  );
}
